import { Head, Link, useForm } from '@inertiajs/react'
import { withSignedInLayout } from '../../signed_in/SignedInLayout.jsx'
import { Card } from '../../components/core/Card.jsx'
import { Badge } from '../../components/core/Badge.jsx'
import { Button } from '../../components/core/Button.jsx'
import { Radio } from '../../components/forms/Radio.jsx'
import { MetricBar } from '../../components/data/MetricBar.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'
// The support address, from the one file that publishes it — the same `legal`
// the terms, privacy and refund pages read, so a customer told to email us here
// and a customer reading the terms are given one address rather than two.
import { legal } from '../../marketing/legalData'

// GET /billing. Nine states on one page, chosen from props, never from a local flag.
// Checkout and cancellation are Stripe's pages: this page posts and the browser leaves.
//   POST /billing/checkout  { plan: 'starter'|'growth'|'agency', interval: 'month'|'year' }
//   POST /billing/portal    no body
// Class 2 sentences arrive assembled (stop_sentence, cancelling, retention,
// annual_refund, annual_before_purchase) and are printed, never composed here.
// Stopping collection never removes anything: every stop renders as a neutral
// Alert, never danger, never a countdown.

const STATUS = {
  none: ['neutral', 'No plan'],
  trialing: ['signal', 'Trial'],
  active: ['signal', 'Active'],
  past_due: ['neutral', 'Payment did not go through'],
  canceled: ['neutral', 'Canceled'],
  // An account the founder gave free access to — admin screen, 2026-10-12.
  comped: ['signal', 'Complimentary'],
}

export default function BillingShow({
  subscription_status = 'none',
  stop = null,
  stop_sentence = null,
  trial = null,
  renews_on = null,
  cancel_on = null,
  manage_billing = false,
  plan = null,
  interval = null,
  plans = [],
  annual_months_billed = 10,
  cohort_current = true,
  usage = null,
  brand_count = 0,
  cancelling = '',
  retention = '',
  annual_refund = null,
  annual_before_purchase = '',
  // `{ until: '30 Nov 2026' | null, active }` for a complimentary account, else
  // null. Labels rather than sentences: what it is, which plan, and when it ends.
  comp = null,
  // `{ plan, interval }` to select on arrival — a "Buy" button on /pricing, or
  // the plan an account that signed up to buy chose there. Else null.
  chosen = null,
  // The one offer of a trial on this page, under the plans: true only for an
  // account that has never had a trial or a plan.
  trial_offer = false,
  trial_days = null,
  errors = {},
}) {
  const checkout = useForm({
    plan: (chosen && chosen.plan) || (plan && plan.key) || '',
    interval: (chosen && chosen.interval) || interval || 'month',
  })
  const portal = useForm({})
  const trialStart = useForm({})

  const subscribed = subscription_status === 'active' || subscription_status === 'past_due'
  const canManage = manage_billing && cohort_current
  const showTrial = trial && !trial.expired && stop !== 'trial_ended'
  const [tone, statusLabel] = STATUS[subscription_status] || STATUS.none
  // In words, the way every other page says it — "twelve months for the price
  // of ten" — from the months the server says an annual plan bills.
  const billedMonths = MONTH_WORDS[annual_months_billed] || String(annual_months_billed)

  function openPortal(e) {
    e.preventDefault()
    portal.post('/billing/portal', { preserveScroll: true })
  }

  function startCheckout(e) {
    e.preventDefault()
    checkout.post('/billing/checkout', { preserveScroll: true })
  }

  function startTrial(e) {
    e.preventDefault()
    trialStart.post('/billing/trial')
  }

  return (
    <>
      <Head title="Plan" />
      <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <header className="mb-8 flex flex-col gap-3">
          <h1 className="page-title">Plan</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={tone}>{statusLabel}</Badge>
            {plan && subscribed && <Badge tone="neutral">{plan.label}</Badge>}
            {comp && plan && <Badge tone="neutral">{plan.label}</Badge>}
            {comp && (
              <Badge tone="neutral">
                {comp.until ? `${comp.active ? 'Until' : 'Ended'} ${comp.until}` : 'No end date'}
              </Badge>
            )}
          </div>
        </header>

        <div className="flex flex-col gap-6">
          {/* Not for `no_plan`: this page is the plan picker, so the sentence
              would announce the screen it sits on. */}
          {stop && stop_sentence && stop !== 'no_plan' && (
            <Alert
              tone="neutral"
              icon={stop === 'past_due' ? 'credit-card' : stop === 'held' ? 'mail' : 'info'}
              title={stop === 'held' ? 'Collection has not started' : 'Collection is not running'}
              actions={
                stop === 'held' ? (
                  <Button variant="secondary" size="sm" href="/verify">
                    Confirm your email
                  </Button>
                ) : null
              }
            >
              {stop_sentence}
            </Alert>
          )}

          {errors.portal && (
            <Alert tone="neutral" icon="alert-triangle" title="Stripe did not open">
              {errors.portal}
            </Alert>
          )}

          {showTrial && <TrialCard trial={trial} />}

          {(subscribed || cancel_on) && plan && (
            <Card
              title={`${plan.label} plan`}
              description={
                interval === 'year'
                  ? `${money(plan.annual_cents)} a year`
                  : `${money(plan.monthly_cents)} a month`
              }
            >
              <div className="flex flex-col gap-5">
                <dl className="flex flex-col">
                  <Row label="Billing" value={interval === 'year' ? 'Annual' : 'Monthly'} />
                  {cancel_on ? (
                    <Row label="Plan ends" value={humanDate(cancel_on)} />
                  ) : (
                    renews_on && <Row label="Renews" value={humanDate(renews_on)} />
                  )}
                  <Row label="Businesses tracked" value={brand_count} />
                  <Row label="Businesses the plan covers" value={plan.businesses} last />
                </dl>
                {canManage && (
                  <form onSubmit={openPortal} className="flex flex-col gap-2">
                    <div>
                      <Button
                        variant={stop === 'past_due' ? 'primary' : 'secondary'}
                        iconRight="arrow-up-right"
                        as="button"
                        loading={portal.processing}
                      >
                        {stop === 'past_due' ? 'Update payment method on Stripe' : 'Manage subscription on Stripe'}
                      </Button>
                    </div>
                    <p className="max-w-[60ch] text-[13.5px] text-[color:var(--text-muted)]">
                      Plan changes, the card and the cancellation are handled on Stripe. The page looks
                      like Stripe rather than Spruce.
                    </p>
                  </form>
                )}
                {/*
                  **A subscribed account on a stale price cohort rendered
                  nothing at all here**, found 2026-10-09 by the tone pass. The
                  portal is refused — correctly, because a default configuration
                  would offer today's annual price to somebody holding
                  yesterday's — but the refusal was expressed by *omitting the
                  button*, so a paying customer who wanted to change their card
                  or cancel saw no control and no sentence explaining why.

                  `PROJECT_STATE` says the guard "refuses rather than
                  mispricing, in both controllers" and that "refusing is visible
                  and reversible". It was visible in the controller and
                  invisible on the screen, which is the half the customer has.
                  A cancellation nobody can find is a chargeback, and dispute
                  rate is what Managed Payments eligibility is monitored on — so
                  this is the one dead end on this page that costs more than
                  goodwill.
                */}
                {manage_billing && !cohort_current && (
                  <Alert tone="neutral" icon="info" title="Changes to this plan go through us">
                    Your plan is on a price we no longer list, so Stripe's own page would offer you
                    the wrong one. Email{' '}
                    <a href={`mailto:${legal.supportEmail}`}>{legal.supportEmail}</a> to change your
                    card, move between plans, or cancel, and we will do it at the price you began on.
                  </Alert>
                )}
              </div>
            </Card>
          )}

          {/* Not before a first plan or trial: there is no allowance yet, so a
              bar reading "0 of 1,000" would describe a period that has not begun. */}
          {usage && stop !== 'no_plan' && <UsageCard usage={usage} atQuota={stop === 'quota'} />}

          {!subscribed && (
            <form onSubmit={startCheckout} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <h2 className="text-[20px] font-semibold leading-snug">
                  {subscription_status === 'canceled' ? 'Start a plan again' : 'Choose a plan'}
                </h2>
                <p className="max-w-[66ch] text-[13.5px] text-[color:var(--text-muted)]">
                  Plans are sized in businesses. One business is one city and one set of questions.
                </p>
              </div>

              <fieldset className="flex flex-col gap-3">
                <legend className="mb-2 text-[13.5px] font-medium">Billing</legend>
                <Radio
                  name="interval"
                  value="month"
                  label="Monthly"
                  description="Billed each month."
                  checked={checkout.data.interval === 'month'}
                  onChange={() => checkout.setData('interval', 'month')}
                />
                <Radio
                  name="interval"
                  value="year"
                  label="Annual"
                  description={`Billed once a year, up front: twelve months for the price of ${billedMonths}.`}
                  checked={checkout.data.interval === 'year'}
                  onChange={() => checkout.setData('interval', 'year')}
                />
                <p className="ml-7 max-w-[62ch] text-[13.5px] text-[color:var(--text-muted)]">
                  {annual_before_purchase}
                </p>
              </fieldset>

              <fieldset className="grid gap-4 md:grid-cols-3">
                <legend className="mb-2 text-[13.5px] font-medium">Plan</legend>
                {plans.map((p) => {
                  const selected = checkout.data.plan === p.key
                  const year = checkout.data.interval === 'year'
                  return (
                    <Card key={p.key} variant={selected ? 'signal' : 'default'} pad="md">
                      <div className="flex flex-col gap-4">
                        <Radio
                          name="plan"
                          value={p.key}
                          label={p.label}
                          description={p.businesses === 1 ? '1 business' : `${p.businesses} businesses`}
                          checked={selected}
                          onChange={() => checkout.setData('plan', p.key)}
                        />
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono text-[28px] font-semibold tabular-nums text-[color:var(--text-strong)]">
                            {money(year ? p.annual_cents : p.monthly_cents)}
                          </span>
                          <span className="font-mono text-[12px] text-[color:var(--text-muted)]">
                            {year ? 'a year' : 'a month'}
                          </span>
                        </div>
                        <dl className="flex flex-col">
                          <Row label="Queries a month" value={p.monthly_query_limit.toLocaleString('en-US')} last />
                        </dl>
                        {/* "Previous" is right for somebody choosing again after a
                            plan ended, and wrong for a complimentary account,
                            which is on that plan now at no charge. */}
                        {p.current && <Badge tone="neutral">{comp && comp.active ? 'Your plan now, at no charge' : 'Your previous plan'}</Badge>}
                      </div>
                    </Card>
                  )
                })}
              </fieldset>

              {errors.checkout && (
                <Alert tone="neutral" icon="alert-triangle" title="Checkout did not open">
                  {errors.checkout}
                </Alert>
              )}

              {cohort_current ? (
                <div className="flex flex-col gap-2">
                  <div>
                    <Button
                      variant="primary"
                      size="lg"
                      iconRight="arrow-up-right"
                      as="button"
                      disabled={!checkout.data.plan}
                      loading={checkout.processing}
                    >
                      Continue to Stripe
                    </Button>
                  </div>
                  {/*
                    This line read "Payment is taken on Stripe's page. Card
                    details are never entered here." The second sentence is tone
                    rule 2 — the button beside it already says Continue to Stripe
                    with an external-link icon, so nobody reading it had wondered
                    where their card was going until we raised it. `copy-rules.md`
                    states the test: would a reader have wondered this if we had
                    not raised it? Replaced rather than deleted, because the
                    forward half of the fact is worth saying — rule 1 — and what
                    the reader gets is a plan, not an absence of risk.
                  */}
                  <p className="max-w-[60ch] text-[13.5px] text-[color:var(--text-muted)]">
                    Payment is taken on Stripe's page, and your plan starts as soon as it goes through.
                  </p>
                </div>
              ) : (
                /*
                  The price-cohort refusal. It read "A plan cannot be started
                  from this account yet" over "Nothing already collected is
                  affected" — a door closing (rule 6) with no way through
                  (rule 9), reassuring about data that was never at risk
                  (rule 2). Nobody is in this state today: it is reachable only
                  once a price rises and a `_v2` set exists, which is exactly
                  when somebody meets it with a card in their hand.

                  The promise is the one the product already makes.
                  `signup_price_cents` records the price somebody was shown on
                  the day their trial began and the adapter charges that, so
                  being refused here is us lacking a Price object rather than us
                  declining to honour it. Saying so is accurate, and accuracy is
                  what makes it sayable.
                */
                <Alert tone="neutral" icon="info" title="Starting a plan here needs a hand from us">
                  Your account was set up at a price we no longer list, so checkout would charge the
                  wrong one. Email{' '}
                  <a href={`mailto:${legal.supportEmail}`}>{legal.supportEmail}</a> and we will start
                  your plan at the price you began on.
                </Alert>
              )}
            </form>
          )}

          {/*
            The one place this page offers a trial, 2026-10-27, decided with the
            founder: a separate line under the plans, never inside a plan card,
            and only for an account that signed up to buy and has never had a
            trial or a plan. Secondary, so buying stays the main choice here.
          */}
          {trial_offer && (
            <form
              onSubmit={startTrial}
              className="flex flex-col gap-3 border-t border-[color:var(--border-subtle)] pt-6 sm:flex-row sm:items-center sm:gap-4"
            >
              <p className="text-[15px] text-[color:var(--text-body)]">
                Not ready to buy? Try Spruce free for {trial_days} days, no credit card.
              </p>
              <div>
                <Button variant="secondary" as="button" loading={trialStart.processing}>
                  Start free trial
                </Button>
              </div>
            </form>
          )}

          {(subscribed || cancel_on) && (
            <Card
              title={cancel_on ? 'Canceled at Stripe' : 'Canceling'}
              description={cancel_on ? `The plan ends on ${humanDate(cancel_on)}` : 'Taken on Stripe, and it removes nothing'}
            >
              <div className="flex flex-col gap-4">
                <p className="max-w-[68ch] text-[13.5px] text-[color:var(--text-body)]">{cancelling}</p>
                {annual_refund && (
                  <p className="max-w-[68ch] text-[13.5px] text-[color:var(--text-body)]">{annual_refund}</p>
                )}
                {canManage && !cancel_on && (
                  <form onSubmit={openPortal}>
                    <Button variant="ghost" iconRight="arrow-up-right" as="button" loading={portal.processing}>
                      Cancel on Stripe
                    </Button>
                  </form>
                )}
              </div>
            </Card>
          )}

          {/*
            The retention promise, and the way out of it.

            **Cancelling is taken at Stripe, so there is no screen of ours to put
            a "delete my data too" option on** — the customer leaves for the
            portal and comes back here. This page is therefore the only moment we
            own where somebody who has just cancelled is reading about what is
            kept, which is exactly when the choice to remove it is wanted.

            `DELETE /account` has existed and cascaded since 2026-09-20, behind
            re-entry of the password. Nothing pointed at it from here, so the
            retention sentence stated what we keep and offered no way to decline.

            Class 1, and deliberately **navigational only**: what deletion
            removes and keeps is a sanctioned Class 2 string on the account page
            itself, and three promises paraphrased into a signpost is how two
            surfaces end up saying different things. This says where, not what.

            **Shown only where leaving is the subject, from 2026-10-09.** It
            rendered unconditionally, so it sat under the plan picker — answering
            an exit question at the moment somebody is deciding to join, which is
            `copy-rules.md`'s own test: would a reader have wondered this if we
            had not raised it? The retention window belongs on `/privacy`, on
            `/refund`, and here in the state the 2026-10-06 decision named — the
            one moment we own where somebody who has just cancelled is reading
            about what is kept, because the cancellation itself is taken at
            Stripe.

            The deletion signpost travels with it rather than staying behind:
            the two are one answer to "what happens to my data, and how do I get
            out", and the account page is in the nav for everybody else.
          */}
          {(subscribed || cancel_on || subscription_status === 'canceled') && (
            <div className="flex flex-col gap-2">
              <p className="max-w-[68ch] text-[13.5px] text-[color:var(--text-muted)]">{retention}</p>
              <p className="max-w-[68ch] text-[13.5px] text-[color:var(--text-muted)]">
                You can delete your account at any time, on the{' '}
                <Link href="/account/edit" className="underline underline-offset-2">account page</Link>.
              </p>
            </div>
          )}
        </div>
      </main>
    </>
  )
}

BillingShow.layout = withSignedInLayout

function TrialCard({ trial }) {
  const weeks = Math.floor(trial.length / 7)
  return (
    <Card
      variant={trial.ending ? 'signal' : 'default'}
      title={trial.ending ? 'The trial is ending' : 'Trial'}
      description={`Day ${trial.day} of ${trial.length}`}
    >
      <div className="flex flex-col gap-4">
        {trial.ending && weeks >= 2 && (
          <p className="max-w-[64ch] text-[15px] text-[color:var(--text-strong)]">
            Collection runs weekly, so a {trial.length}-day trial covers {weeks} collections. Two readings
            can be compared; the third is the first that shows whether anything moved.
          </p>
        )}
        <dl className="flex flex-col">
          <Row label="Trial ends" value={humanDate(trial.ends_on)} />
          <Row label="Days left" value={`${trial.days_remaining} of ${trial.length}`} />
          <Row label="Card" value={trial.card_on_file ? 'On file' : 'Not on file'} last />
        </dl>
      </div>
    </Card>
  )
}

function UsageCard({ usage, atQuota }) {
  return (
    <Card title="This period" description={`Resets ${humanDate(usage.resets_on)}`}>
      <div className="flex flex-col gap-3">
        <MetricBar
          name="Queries used"
          value={usage.used}
          max={usage.limit}
          display={`${usage.used.toLocaleString('en-US')} of ${usage.limit.toLocaleString('en-US')}`}
          tone={atQuota ? 'signal' : 'data'}
        />
        <p className="max-w-[64ch] text-[13.5px] text-[color:var(--text-muted)]">
          A plan's full business count uses most of its allowance by design, so a bar near the end is
          the plan working as sold.
        </p>
      </div>
    </Card>
  )
}

function Row({ label, value, last = false }) {
  return (
    <div
      className={`flex items-baseline justify-between gap-3 py-2 ${
        last ? '' : 'border-b border-[color:var(--border-subtle)]'
      }`}
    >
      <dt className="text-[13.5px] text-[color:var(--text-body)]">{label}</dt>
      <dd className="font-mono text-[13.5px] tabular-nums text-[color:var(--text-strong)]">{value}</dd>
    </div>
  )
}

const MONTH_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve']

function money(cents) {
  const whole = cents % 100 === 0
  return '$' + (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })
}

// Dates arrive formatted. The server sends `to_fs(:human)` — the same format the
// dashboard uses and, more importantly, the format inside the sanctioned
// "Collection is paused until {date}" sentence, which is assembled in Ruby and
// cannot be reformatted here. A second format on this page would put two
// spellings of one date on one screen.
function humanDate(s) {
  return s || ''
}
